import { useState, useMemo, useEffect } from "react";
import useContactingManager from "../hooks/useContactingManager";
import useWhatsApp from "../hooks/useWhatsApp";
import LoadingCircle from "../../../components/Global/LoadingCircle";
import AdminBlock from "./AdminBlock";
import MemberStatusList from "./MemberStatusList";
import ContactMessagePopup from "./ContactMessagePopup/ContactMessagePopup";
import { WhatsAppStatusIndicator, WhatsAppQRModal } from "./WhatsAppStatus/WhatsAppStatus";
import SendResultsModal from "./SendResultsModal/SendResultsModal";
import strings from "../../../locales/ro.json";
import "./ContactingTab.css";

const ContactingTab = () => {
  const {
    dangerUsers,
    derankUsers,
    loading,
    error,
    clearError,
    buildRecipients,
    getMissingPhoneUsers,
  } = useContactingManager();

  const {
    whatsappStatus,
    qrCode,
    connectSSE,
    disconnectSSE,
    disconnectWhatsApp,
    sending,
    sendResults,
    sendMessages,
    clearSendResults,
  } = useWhatsApp();

  const s = strings.admin.contacting;
  const ws = s.whatsapp;

  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [sendingError, setSendingError] = useState(null);

  useEffect(() => {
    connectSSE();
    return () => disconnectSSE();
  }, [connectSSE, disconnectSSE]);

  const contactList = useMemo(() => {
    if (selectedGroup === "danger") return dangerUsers;
    if (selectedGroup === "derank") return derankUsers;
    return [];
  }, [selectedGroup, dangerUsers, derankUsers]);

  const handleGroupChange = (e) => {
    setSelectedGroup(e.target.value);
    setSelectedIds(new Set());
  };

  const handleToggle = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === contactList.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contactList.map((u) => u.id)));
    }
  };

  const handleContactSelected = () => {
    if (whatsappStatus !== "ready") {
      setIsQrModalOpen(true);
      return;
    }
    setIsPopupOpen(true);
  };

  const handleSendContactMessage = async (variables, finalMessage) => {
    setSendingError(null);

    const recipients = buildRecipients(selectedIds, contactList);
    const missingPhone = getMissingPhoneUsers(selectedIds, contactList);

    if (recipients.length === 0) {
      setSendingError(
        missingPhone.length > 0
          ? ws.noPhoneWarning
          : s.contact.emptyGroup
      );
      return;
    }

    const backendMessage = finalMessage.replace(/\*nume\*/g, "{{name}}");

    try {
      await sendMessages(recipients, backendMessage);
      setIsPopupOpen(false);
      setIsResultsOpen(true);
    } catch (err) {
      if (err.status === 409) {
        setIsPopupOpen(false);
        setIsQrModalOpen(true);
      } else {
        setSendingError(err.message || s.errors.contact);
      }
    }
  };

  const handleQrModalClose = () => {
    setIsQrModalOpen(false);
  };

  const handleResultsClose = () => {
    setIsResultsOpen(false);
    clearSendResults();
    setSelectedIds(new Set());
  };

  const activeTemplate = selectedGroup === "danger" ? s.templates.danger : s.templates.derank;
  const sampleName = "*nume*";
  const hasMultipleTemplates = selectedGroup === "danger" && typeof activeTemplate === "object";

  const missingPhoneCount = useMemo(() => {
    return getMissingPhoneUsers(selectedIds, contactList).length;
  }, [selectedIds, contactList, getMissingPhoneUsers]);

  if (loading && dangerUsers.length === 0 && derankUsers.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <LoadingCircle />
      </div>
    );
  }

  return (
    <div className="ct-container">
      {error && (
        <div className="ct-error" onClick={clearError} style={{ cursor: "pointer" }}>
          {error} {s.clickToDismiss}
        </div>
      )}

      {sendingError && (
        <div className="ct-error" onClick={() => setSendingError(null)} style={{ cursor: "pointer" }}>
          {sendingError} {s.clickToDismiss}
        </div>
      )}

      <AdminBlock
        title={`${s.tabs.danger} (${dangerUsers.length})`}
        defaultOpen={false}
      >
        <MemberStatusList
          users={dangerUsers}
          emptyMessage={s.list.emptyDanger}
          variant="danger"
        />
      </AdminBlock>

      <AdminBlock
        title={`${s.tabs.derank} (${derankUsers.length})`}
        defaultOpen={false}
      >
        <MemberStatusList
          users={derankUsers}
          emptyMessage={s.list.emptyDerank}
          variant="derank"
        />
      </AdminBlock>

      <AdminBlock 
        title={s.tabs.contact} 
        defaultOpen={true}
        headerElement={
          <WhatsAppStatusIndicator
            status={whatsappStatus}
            onClick={() => setIsQrModalOpen(true)}
          />
        }
      >
        <div className="ct-contact-section">
          <div className="ct-select-wrapper">
            <label className="ct-select-label">{s.contact.groupSelectLabel}</label>
            <select
              className="ct-select"
              value={selectedGroup}
              onChange={handleGroupChange}
            >
              <option value="">{s.contact.groupSelectPlaceholder}</option>
              <option value="danger">{s.contact.groupDanger}</option>
              <option value="derank">{s.contact.groupDerank}</option>
            </select>
          </div>

          {!selectedGroup ? (
            <p className="ct-empty">{s.contact.noGroupSelected}</p>
          ) : contactList.length === 0 ? (
            <p className="ct-empty">{s.contact.emptyGroup}</p>
          ) : (
            <>
              <div className="ct-action-bar">
                <label className="ct-select-all-wrapper">
                  <div className="ct-checkbox-wrapper">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === contactList.length && contactList.length > 0}
                      onChange={handleSelectAll}
                    />
                    <span className="ct-checkmark"></span>
                  </div>
                  <span className="ct-select-all-text">{s.contact.selectAll}</span>
                </label>
                <span className="ct-selected-count">
                  {selectedIds.size} / {contactList.length} {s.contact.selectedCount}
                </span>
              </div>

              {missingPhoneCount > 0 && selectedIds.size > 0 && (
                <div className="ct-phone-warning">
                  ⚠ {missingPhoneCount} {missingPhoneCount === 1 ? "membru nu are" : "membri nu au"} număr de telefon setat.
                </div>
              )}

              <MemberStatusList
                users={contactList}
                emptyMessage={s.contact.emptyGroup}
                variant={selectedGroup}
                selectable={true}
                selectedIds={selectedIds}
                onToggle={handleToggle}
              />

              <div className="ct-contact-actions">
                <button
                  className="ct-contact-btn primary"
                  onClick={handleContactSelected}
                  disabled={selectedIds.size === 0 || sending}
                  type="button"
                >
                  {sending ? ws.sendingMessages : s.contact.contactBtn}
                </button>
              </div>
            </>
          )}
        </div>
      </AdminBlock>

      <ContactMessagePopup
        isOpen={isPopupOpen}
        template={hasMultipleTemplates ? null : activeTemplate}
        templates={hasMultipleTemplates ? activeTemplate : null}
        sampleName={sampleName}
        onClose={() => setIsPopupOpen(false)}
        onSend={handleSendContactMessage}
        sending={sending}
      />

      <WhatsAppQRModal
        isOpen={isQrModalOpen}
        onClose={handleQrModalClose}
        qrCode={qrCode}
        status={whatsappStatus}
        onConnect={connectSSE}
        onDisconnect={disconnectWhatsApp}
      />

      <SendResultsModal
        isOpen={isResultsOpen}
        results={sendResults}
        onClose={handleResultsClose}
      />
    </div>
  );
};

export default ContactingTab;
